import React, { useEffect, useState } from 'react';
import { Card, Layout, Table, Tag, Typography, message } from 'antd';
import { collection, getDocs, orderBy, query, where, getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/DataContext';
import dayjs from 'dayjs';

const { Content } = Layout;
const { Title, Text } = Typography;

const formatRupiah = (amount) =>
    new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(amount);

const PublicCustomersList = () => {
    const { currentEventId } = useAuth();
    const [customers, setCustomers] = useState([]);
    const [venuesMap, setVenuesMap] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (currentEventId) {
            fetchCustomers();
        }
    }, [currentEventId]);

    const fetchCustomers = async () => {
        setLoading(true);
        try {
            const q = query(
                collection(db, 'publicBookings'), 
                where('eventId', '==', currentEventId),
                orderBy('createdAt', 'desc')
            );
            const querySnapshot = await getDocs(q);
            const data = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            const eventRef = doc(db, 'events', currentEventId);
            const eventSnap = await getDoc(eventRef);
            if (eventSnap.exists()) {
                const eventData = eventSnap.data();
                if (eventData.venues) {
                    const vMap = {};
                    eventData.venues.forEach(v => { vMap[v.id] = v.label; });
                    setVenuesMap(vMap);
                }
            }

            setCustomers(data);
        } catch (error) {
            console.error('Error fetching customers:', error);
            message.error('Failed to load customers');
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Booking ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'Name',
            dataIndex: 'userName',
            key: 'userName',
        },
        {
            title: 'Email',
            dataIndex: 'userEmail',
            key: 'userEmail',
        },
        {
            title: 'Phone',
            dataIndex: 'userPhone',
            key: 'userPhone',
        },
        {
            title: 'Venue',
            dataIndex: 'venue',
            key: 'venue',
            render: (venue) => venuesMap[venue] || 'APCS Venue', 
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
        },
        {
            title: 'Session',
            dataIndex: 'session',
            key: 'session',
        },
        {
            title: 'Total Amount',
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            render: (amount) => formatRupiah(amount),
        },
        {
            title: 'Payment Status',
            dataIndex: 'paymentStatus',
            key: 'paymentStatus',
            render: (status) => {
                let color = 'default';
                if (status === 'PAID' || status === 'paid') color = 'green';
                else if (status === 'pending') color = 'orange';
                else if (status === 'expired') color = 'red';
                
                return (
                    <Tag color={color}>
                        {status ? status.toUpperCase() : 'UNKNOWN'}
                    </Tag>
                );
            },
        },
        {
            title: 'Seats',
            dataIndex: 'selectedSeatIds',
            key: 'selectedSeatIds',
            render: (seats) => (seats && seats.length > 0) ? seats.map(s => s.split('_')[0]).join(', ') : '-',
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (timestamp) => timestamp ? dayjs(timestamp.toDate()).format('DD MMM YYYY HH:mm') : '-',
        },
    ];

    return (
        <Content style={{ padding: '24px 50px', minHeight: 280 }}>
            <Title level={2}>Public Ticket Bookings</Title>
            <Text type="secondary">List of all public ticket bookings and their payment status for the current event.</Text>

            <Card style={{ marginTop: '24px' }}>
                <Table
                    columns={columns}
                    dataSource={customers}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 1200 }}
                />
            </Card>
        </Content>
    );
};

export default PublicCustomersList;
